import { Button, ButtonGroup, Tooltip, Typography } from '@material-ui/core';
import { Sort, SortByAlpha } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { sortBy } from 'lodash';
import React from 'react';

import { buildStackedHorizontalBarOption } from 'shared/common/charts/options';

import { echarts, ReactEChartsCore } from '../../common/charts/echarts-core';
import { usePortalTooltip } from '../../common/charts/tooltip';
import {
    clientActivityFields,
    ClientMetrics,
    customTooltip,
    defaultAggregateViewSelected,
    Field,
    FieldsSelected,
    getChartHeight,
    getFieldColors,
    getFieldLabel,
    getMetricsByClient
} from '../../common/metrics';
import { MetricsData } from '../../graphql/queries/metrics';
import { useChartExport } from '../../hooks/use-chart-export';
import { useLocalStorage } from '../../hooks/use-local-storage';
import { ChartExportButtons } from '../charts/chart-export-buttons';
import { Legend } from './legend';

export const UserClientMetrics: React.FC<{ metrics?: MetricsData[] }> = ({ metrics }) => {
    const [variant, setVariant] = useLocalStorage<'cost' | 'count'>('agg-metrics-variant', 'count');
    const [selectedFields, setSelectedFields] = useLocalStorage<FieldsSelected>(
        'agg-metrics-fields',
        defaultAggregateViewSelected
    );
    const [hoveredField, setHoveredField] = React.useState<Field | undefined>(undefined);
    const [sort, setSort] = useLocalStorage<'alpha' | 'sum'>('agg-metrics-sort', 'alpha');
    const chartExport = useChartExport({ title: 'activity-by-client' });

    const handleVariantChange = (value: 'cost' | 'count') => () => {
        setVariant(value);
    };

    const handleSortChange = (value: 'alpha' | 'sum') => () => {
        setSort(value);
    };

    const fields = React.useMemo(() => clientActivityFields.filter((f) => selectedFields[f]), [selectedFields]);

    const safeMetrics = React.useMemo(() => metrics ?? [], [metrics]);

    const getClientFieldTotal = React.useCallback(
        (entry: ClientMetrics) => fields.reduce((acc, f) => acc + (entry.metrics[f]?.value ?? 0), 0),
        [fields]
    );

    const metricsByClientVariant = React.useMemo(
        () => getMetricsByClient(safeMetrics, variant).filter((entry) => getClientFieldTotal(entry) > 0),
        [getClientFieldTotal, safeMetrics, variant]
    );

    const metricsByClientCounts = React.useMemo(() => getMetricsByClient(safeMetrics, 'count'), [safeMetrics]);

    const countsByClientId = React.useMemo(() => {
        const map = new Map<string, ClientMetrics>();
        metricsByClientCounts.forEach((entry) => {
            map.set(entry.clientId, entry);
        });
        return map;
    }, [metricsByClientCounts]);

    const sortedMetrics = React.useMemo(
        () => sortBy(metricsByClientVariant, (m) => (sort === 'alpha' ? m.client : -getClientFieldTotal(m))),
        [getClientFieldTotal, metricsByClientVariant, sort]
    );

    const sortedCountMetrics = React.useMemo(
        () => sortedMetrics.map((metric) => countsByClientId.get(metric.clientId) ?? metric),
        [countsByClientId, sortedMetrics]
    );

    const categories = sortedMetrics.map((v) => v.client);
    const series = React.useMemo(
        () =>
            fields.map((f) => ({
                data: sortedMetrics.map((metric, index) => {
                    const weightedValue = metric.metrics[f]?.value ?? 0;
                    const countValue = sortedCountMetrics[index]?.metrics?.[f]?.value ?? weightedValue;
                    return {
                        labelValue: countValue,
                        value: weightedValue
                    };
                }),
                name: getFieldLabel(f)
            })),
        [fields, sortedCountMetrics, sortedMetrics]
    );

    const countSeries = React.useMemo(
        () =>
            fields.map((f) => ({
                data: sortedCountMetrics.map((m) => m?.metrics?.[f]?.value ?? 0),
                name: getFieldLabel(f)
            })),
        [fields, sortedCountMetrics]
    );

    const colors = getFieldColors(fields, hoveredField);

    const tooltipTemplate = React.useMemo(
        () => customTooltip(sortedCountMetrics ?? [], fields, countSeries),
        [countSeries, fields, sortedCountMetrics]
    );

    const tooltip = usePortalTooltip(
        {
            formatter: (raw: any) => {
                const params = Array.isArray(raw) ? raw[0] : raw;
                const dataIndex = params?.dataIndex ?? 0;
                const seriesIndex = params?.seriesIndex ?? 0;
                return tooltipTemplate({ dataPointIndex: dataIndex, seriesIndex });
            },
            trigger: 'item'
        },
        [tooltipTemplate]
    );

    const chartOptions = React.useMemo(() => {
        if (!categories.length || !series.length) {
            return undefined;
        }
        return buildStackedHorizontalBarOption({
            categories,
            colors,
            series,
            tooltip
        });
    }, [categories, colors, series, tooltip]);

    const tabs = (
        <div className="view-selectors">
            <ButtonGroup size="small" variant="text">
                <Tooltip title="Sort Alphabetical">
                    <Button className={sort === 'alpha' ? '' : 'inactive'} onClick={handleSortChange('alpha')}>
                        <SortByAlpha fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Sort by Activity">
                    <Button className={sort === 'sum' ? '' : 'inactive'} onClick={handleSortChange('sum')}>
                        <Sort fontSize="small" />
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <ButtonGroup size="small" variant="text">
                <Tooltip title="Individual Action Counts">
                    <Button className={variant === 'count' ? '' : 'inactive'} onClick={handleVariantChange('count')}>
                        Raw Counts
                    </Button>
                </Tooltip>
                <Tooltip title="Weighted sums based on estimated action time">
                    <Button className={variant === 'cost' ? '' : 'inactive'} onClick={handleVariantChange('cost')}>
                        Weighted Sums
                    </Button>
                </Tooltip>
            </ButtonGroup>
        </div>
    );

    const chart = !metrics ? (
        <Skeleton variant="rect" />
    ) : categories.length > 0 && chartOptions ? (
        <ReactEChartsCore
            ref={chartExport.chartRef}
            echarts={echarts}
            option={chartOptions}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: getChartHeight(categories?.length ?? 0) }}
            opts={{ renderer: 'svg' }}
        />
    ) : null;

    const canExport = Boolean(metrics && chartOptions && categories.length > 0);

    return (
        <div className="chart-container">
            <div className="panel-header">
                <div className="panel-title">
                    <Typography variant="h5">Activity by Client</Typography>
                </div>
                <div className="panel-options">
                    {tabs}
                    <ChartExportButtons
                        disabled={!canExport}
                        isExporting={chartExport.isExporting}
                        onExportPng={chartExport.exportAsPng}
                        onExportSvg={chartExport.exportAsSvg}
                    />
                </div>
            </div>
            <div className="panel-content">
                <div className="chart">{chart}</div>
                <Legend
                    fields={clientActivityFields}
                    selected={selectedFields}
                    onFieldHover={setHoveredField}
                    onUpdateSelected={setSelectedFields}
                />
            </div>
        </div>
    );
};
